/**
 * BI Event Name Constants
 * Centralized event names to avoid typos and enable easy refactoring.
 */

import { UserManagementEntityType } from './types';

// ============= Auth & Session =============
export const BI_LOGIN_SUCCESS = 'login_success';
export const BI_LOGOUT = 'logout';
export const BI_AUTH_SSO_ERROR_VIEW = 'auth_sso_error_view';
export const BI_UNAUTHORIZED_PAGE_VIEW = 'unauthorized_page_view';

// ============= Errors =============
export const BI_GENERIC_ERROR_PAGE_VIEW = 'generic_error_page_view';

// ============= Navigation =============
export const BI_NOT_FOUND_PAGE_VIEW = 'not_found_page_view';
export const BI_SITE_LIST_VIEW = 'site_list_view';
export const BI_PROVIDER_LIST_VIEW = 'provider_list_view';
export const BI_SITE_DETAIL_VIEW = 'site_detail_view';
export const BI_PROVIDER_DETAIL_VIEW = 'provider_detail_view';

// ============= Site List Interaction =============
export const BI_SITE_LIST_SEARCH = 'site_list_search';
export const BI_SITE_LIST_FILTER_OPEN = 'site_list_filter_open';
export const BI_SITE_LIST_FILTER_APPLY = 'site_list_filter_apply';
export const BI_SITE_LIST_FILTER_CLEAR = 'site_list_filter_clear';
export const BI_SITE_SHOW_INACTIVE_TOGGLE = 'site_show_inactive_toggle';
export const BI_SITE_LIST_SORT = 'site_list_sort';
export const BI_SITE_LIST_PAGINATE = 'site_list_paginate';
export const BI_SITE_LIST_PAGE_SIZE_CHANGE = 'site_list_page_size_change';
export const BI_SITE_LIST_EXPORT_CSV = 'site_list_export_csv';
export const BI_SITE_LIST_LOAD_ERROR = 'site_list_load_error';
export const BI_SITE_LIST_RETRY_FETCH = 'site_list_retry_fetch';

// ============= Provider List Interaction =============
export const BI_PROVIDER_LIST_SEARCH = 'provider_list_search';
export const BI_PROVIDER_LIST_FILTER_OPEN = 'provider_list_filter_open';
export const BI_PROVIDER_LIST_FILTER_APPLY = 'provider_list_filter_apply';
export const BI_PROVIDER_LIST_FILTER_CLEAR = 'provider_list_filter_clear';
export const BI_PROVIDER_SHOW_INACTIVE_TOGGLE = 'provider_show_inactive_toggle';
export const BI_PROVIDER_LIST_SORT = 'provider_list_sort';
export const BI_PROVIDER_LIST_PAGINATE = 'provider_list_paginate';
export const BI_PROVIDER_LIST_PAGE_SIZE_CHANGE = 'provider_list_page_size_change';
export const BI_PROVIDER_LIST_EXPORT_CSV = 'provider_list_export_csv';
export const BI_PROVIDER_LIST_LOAD_ERROR = 'provider_list_load_error';
export const BI_PROVIDER_LIST_RETRY_FETCH = 'provider_list_retry_fetch';

// ============= Site Management (CRUD) =============
export const BI_SITE_CREATE_START = 'site_create_start';
export const BI_SITE_CREATE_SUBMIT = 'site_create_submit';
export const BI_SITE_UPDATE_SUBMIT = 'site_update_submit';
export const BI_SITE_DEACTIVATE_SUBMIT = 'site_deactivate_submit';

// ============= Provider Management (CRUD) =============
export const BI_PROVIDER_CREATE_START = 'provider_create_start';
export const BI_PROVIDER_CREATE_SUBMIT = 'provider_create_submit';
export const BI_PROVIDER_UPDATE_SUBMIT = 'provider_update_submit';
export const BI_PROVIDER_DEACTIVATE_SUBMIT = 'provider_deactivate_submit';
export const BI_PROVIDER_REACTIVATE_SUBMIT = 'provider_reactivate_submit';

// ============= Entity-based helpers =============

/**
 * Get the list view event name for an entity type
 */
export const getListViewEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_VIEW : BI_SITE_LIST_VIEW;

/**
 * Get the detail view event name for an entity type
 */
export const getDetailViewEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_DETAIL_VIEW : BI_SITE_DETAIL_VIEW;

/**
 * Get the search event name for an entity type
 */
export const getSearchEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_SEARCH : BI_SITE_LIST_SEARCH;

/**
 * Get the sort event name for an entity type
 */
export const getSortEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_SORT : BI_SITE_LIST_SORT;

/**
 * Get the paginate event name for an entity type
 */
export const getPaginateEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_PAGINATE : BI_SITE_LIST_PAGINATE;

/**
 * Get the page size change event name for an entity type
 */
export const getPageSizeChangeEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_PAGE_SIZE_CHANGE : BI_SITE_LIST_PAGE_SIZE_CHANGE;

/**
 * Get the export CSV event name for an entity type
 */
export const getExportCsvEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_EXPORT_CSV : BI_SITE_LIST_EXPORT_CSV;

/**
 * Get the load error event name for an entity type
 */
export const getLoadErrorEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_LOAD_ERROR : BI_SITE_LIST_LOAD_ERROR;

/**
 * Get the retry fetch event name for an entity type
 */
export const getRetryFetchEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_RETRY_FETCH : BI_SITE_LIST_RETRY_FETCH;

/**
 * Get the create start event name for an entity type
 */
export const getCreateStartEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_CREATE_START : BI_SITE_CREATE_START;

/**
 * Get the create submit event name for an entity type
 */
export const getCreateSubmitEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_CREATE_SUBMIT : BI_SITE_CREATE_SUBMIT;

/**
 * Get the update submit event name for an entity type
 */
export const getUpdateSubmitEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_UPDATE_SUBMIT : BI_SITE_UPDATE_SUBMIT;

/**
 * Get the deactivate submit event name for an entity type
 */
export const getDeactivateSubmitEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_DEACTIVATE_SUBMIT : BI_SITE_DEACTIVATE_SUBMIT;

/**
 * Get the filter open event name for an entity type
 */
export const getFilterOpenEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_FILTER_OPEN : BI_SITE_LIST_FILTER_OPEN;

/**
 * Get the filter apply event name for an entity type
 */
export const getFilterApplyEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_FILTER_APPLY : BI_SITE_LIST_FILTER_APPLY;

/**
 * Get the filter clear event name for an entity type
 */
export const getFilterClearEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_LIST_FILTER_CLEAR : BI_SITE_LIST_FILTER_CLEAR;

/**
 * Get the show inactive toggle event name for an entity type
 */
export const getShowInactiveToggleEvent = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? BI_PROVIDER_SHOW_INACTIVE_TOGGLE : BI_SITE_SHOW_INACTIVE_TOGGLE;

/**
 * Get the entity ID property key for BI event payloads
 */
export const getEntityIdKey = (entity: UserManagementEntityType): string =>
  entity === UserManagementEntityType.PROVIDERS ? 'providerId' : 'siteId';
